import * as React from 'react'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { cn } from '@/lib/utils'
import { Badge } from '@/components/ui/badge'
import { useViewOnly, useViewOnlyBadge } from './app-shell'

export interface FormFieldProps {
  label: string
  htmlFor?: string
  error?: string
  required?: boolean
  hint?: string
  children: React.ReactNode
}

/** A labelled control with optional hint + inline error — the unit a form is built from. */
export function FormField({ label, htmlFor, error, required, hint, children }: FormFieldProps) {
  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor={htmlFor}>
        {label}
        {required && <span className="text-destructive"> *</span>}
      </Label>
      {children}
      {hint && !error && <p className="text-caption text-muted-foreground">{hint}</p>}
      {error && <p className="text-caption text-destructive">{error}</p>}
    </div>
  )
}

export interface EntityFormProps {
  title: string
  className?: string
  titleClassName?: string
  description?: string
  /** A form-level error (e.g. a duplicate-email refusal from the API). */
  error?: string
  submitLabel?: string
  onSubmit?: (e: React.FormEvent<HTMLFormElement>) => void
  onCancel?: () => void
  submitting?: boolean
  children: React.ReactNode
}

/**
 * The archetype for create/edit screens: a titled card holding stacked FormFields
 * with a form-level error slot and a Save / Cancel footer.
 */
export function EntityForm({
  title,
  className,
  titleClassName,
  description,
  error,
  submitLabel = 'Save',
  onSubmit,
  onCancel,
  submitting,
  children,
}: EntityFormProps) {
  const viewOnly = useViewOnly()
  const showViewOnlyBadge = useViewOnlyBadge()
  return (
    <form onSubmit={onSubmit} className={cn('mx-auto w-full max-w-2xl px-8 py-8', className)}>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between gap-3">
            <CardTitle className={titleClassName}>{title}</CardTitle>
            {showViewOnlyBadge && <Badge variant="info">View only</Badge>}
          </div>
          {description && <CardDescription>{description}</CardDescription>}
        </CardHeader>
        <CardContent className="flex flex-col gap-5">
          {error && (
            <div className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-body-sm text-destructive">
              {error}
            </div>
          )}
          <fieldset disabled={viewOnly} className="contents">
            {children}
          </fieldset>
          {!viewOnly && (
            <div className="flex justify-end gap-2 pt-2">
              {onCancel && (
                <Button type="button" variant="outline" onClick={onCancel}>
                  Cancel
                </Button>
              )}
              <Button type="submit" disabled={submitting}>
                {submitting ? 'Saving…' : submitLabel}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </form>
  )
}
